import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import '../styles/design-system.css';

const HeroModern = () => {
    const { t } = useTranslation();
    const backgroundImages = [
        './images/homes/slide1.jpg',
        './images/homes/slide2.jpg',
        './images/homes/slide3.jpg',
        './images/homes/slide4.jpg',
        './images/homes/slide5.jpg'
    ];

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Preload images to prevent lag
    useEffect(() => {
        backgroundImages.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, [backgroundImages]);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [backgroundImages.length]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            minHeight: '100vh',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #ffffff 50%, #f8f9fa 100%)',
            zIndex: 1 // Base z-index
        }}>

            {/* Main Content */}
            <div className="container hero-content" style={{
                position: 'relative',
                zIndex: 20
            }}>
                {/* Left Text Side */}
                <div className="hero-text-side">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        style={{ display: 'inline-block' }}
                    >
                        <img
                            src="./favicon.png"
                            alt="Sun Lanka"
                            style={{
                                width: '60px',
                                height: '60px',
                                marginBottom: '20px',
                                objectFit: 'contain'
                            }}
                        />
                    </motion.div>

                    <motion.h1
                        className="heading-lg"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        style={{
                            color: 'var(--color-charcoal)',
                            letterSpacing: '-2px'
                        }}
                    >
                        {t('hero_title_main')}<br />
                        <span style={{ fontSize: '0.6em', fontWeight: '300', display: 'block', marginTop: '10px' }}>{t('hero_title_sub')}</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        style={{
                            fontSize: '1.25rem',
                            color: '#333',
                            marginBottom: '40px',
                            letterSpacing: '0.5px',
                            lineHeight: '1.6',
                            maxWidth: '500px',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}
                    >
                        {t('hero_tagline1')}<br />
                        <span style={{ color: 'var(--color-primary-red)', fontWeight: 'bold' }}>{t('hero_tagline2')}</span>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}
                    >
                        <ScrollLink to="about" smooth={true} duration={500} offset={-70}>
                            <button className="btn-primary" style={{ fontSize: '1.1rem', padding: '15px 40px' }}>
                                {t('hero_btn_join')}
                            </button>
                        </ScrollLink>
                        <ScrollLink to="academic" smooth={true} duration={500} offset={-70}>
                            <button className="btn-secondary" style={{ fontSize: '1.1rem', padding: '15px 40px' }}>
                                {t('hero_btn_courses')}
                            </button>
                        </ScrollLink>
                    </motion.div>
                </div>

                {/* Right Image Side - Slideshow */}
                <motion.div
                    className="hero-image-side"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    style={{
                        position: 'relative',
                        width: '100%',
                        borderRadius: '30px',
                        overflow: 'hidden',
                        boxShadow: '0 30px 60px rgba(0,0,0,0.15)',
                        background: '#f0f0f0'
                    }}
                >
                    {/* Optimized CSS Slideshow - All images loaded, only opacity changes */}
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        {backgroundImages.map((src, index) => (
                            <div
                                key={index}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${src})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    opacity: currentImageIndex === index ? 1 : 0,
                                    transition: 'opacity 1.5s ease-in-out', // Smooth CSS transition
                                    zIndex: currentImageIndex === index ? 2 : 1
                                }}
                            />
                        ))}

                        {/* Optional Overlay/Frame effect */}
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            border: '8px solid rgba(255,255,255,0.3)',
                            borderRadius: '30px',
                            zIndex: 10,
                            pointerEvents: 'none'
                        }} />
                    </div>
                </motion.div>
            </div>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                style={{
                    position: 'absolute',
                    bottom: '30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 3,
                    color: '#666',
                    cursor: 'pointer'
                }}
            >
                <div style={{ fontSize: '24px' }}>↓</div>
            </motion.div>

        </div>
    );
};

export default HeroModern;
